import { z } from 'zod';

import { QcFieldType, QcPrefill } from '@nucleus-commerce/api-client/admin';

import { STAGE_KEY } from './workflow-form-schema';

export const MAX_QC_FIELDS = 60;
export const MAX_QC_OPTIONS = 30;
/** The select needs a value for "no prefill"; the mapper turns it into null. */
export const NO_PREFILL = 'none';

export const qcFieldFormSchema = z
  .object({
    key: z
      .string()
      .trim()
      .refine((value) => value === '' || STAGE_KEY.test(value), 'workflows:validation.key'),
    label: z.string().trim().min(1, 'workflows:qc.validation.label').max(200),
    type: z.enum(QcFieldType),
    options: z
      .array(z.object({ value: z.string().trim().max(120) }))
      .max(MAX_QC_OPTIONS, 'workflows:qc.validation.optionsMax'),
    allowOther: z.boolean(),
    required: z.boolean(),
    prefill: z.union([z.enum(QcPrefill), z.literal(NO_PREFILL)]),
  })
  .superRefine((field, ctx) => {
    if (field.type !== 'choice') return;
    const filled = field.options.filter((option) => option.value !== '');
    if (filled.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['options'],
        message: 'workflows:qc.validation.options',
      });
    }
    field.options.forEach((option, index) => {
      if (option.value === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['options', index, 'value'],
          message: 'workflows:qc.validation.optionEmpty',
        });
      }
    });
  });

export const qcFormSchema = z.object({
  name: z.string().trim().min(1, 'workflows:qc.validation.name').max(120),
  fields: z
    .array(qcFieldFormSchema)
    .min(1, 'workflows:qc.validation.fieldsMin')
    .max(MAX_QC_FIELDS, 'workflows:qc.validation.fieldsMax'),
});

export type QcFieldFormValues = z.infer<typeof qcFieldFormSchema>;
export type QcFormInput = z.input<typeof qcFormSchema>;
export type QcFormValues = z.output<typeof qcFormSchema>;

export function emptyQcField(type: QcFieldFormValues['type'] = 'yes_no'): QcFieldFormValues {
  return {
    key: '',
    label: '',
    type,
    options: type === 'choice' ? [{ value: '' }] : [],
    allowOther: false,
    required: true,
    prefill: NO_PREFILL,
  };
}
